import { z } from 'zod';
import { createMap, setForest, type GameMap } from '../sim/map/gameMap';
import { parseOrThrow } from './validate';

const TileChar = z.enum(['.', 'T']);
const Tile = z.object({ x: z.number().int().nonnegative(), y: z.number().int().nonnegative() });

const MapFile = z
  .object({
    name: z.string().min(1),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    legend: z.record(z.string(), z.string()),
    rows: z.array(z.string()),
    /** Top-left tile of each player's starting town center. */
    starts: z.array(Tile).min(1),
    /** Top-left tile and gold amount of each mine. */
    mines: z.array(Tile.extend({ gold: z.number().int().positive() })).default([]),
    /** Lumber on every forest tile. */
    lumberPerTile: z.number().int().min(1).max(65535).default(100),
    /** Units standing on the map at the start (e.g. an enemy camp for the prototype). */
    units: z
      .array(Tile.extend({ def: z.string().min(1), owner: z.number().int().nonnegative() }))
      .default([]),
    /** Each player's ledger at the start of the match. */
    startingResources: z
      .object({ gold: z.number().int().nonnegative(), lumber: z.number().int().nonnegative() })
      .default({ gold: 0, lumber: 0 }),
  })
  .superRefine((m, ctx) => {
    if (m.rows.length !== m.height) {
      ctx.addIssue({ code: 'custom', message: `expected ${m.height} rows, got ${m.rows.length}` });
    }
    m.rows.forEach((row, y) => {
      if (row.length !== m.width) {
        ctx.addIssue({
          code: 'custom',
          message: `row ${y} has ${row.length} tiles, expected ${m.width}`,
        });
      }
      for (const ch of row) {
        if (!TileChar.safeParse(ch).success) {
          ctx.addIssue({ code: 'custom', message: `row ${y} has unknown tile '${ch}'` });
        }
      }
    });
    const check = (label: string, t: { x: number; y: number }, i: number) => {
      if (t.x >= m.width || t.y >= m.height) {
        ctx.addIssue({
          code: 'custom',
          message: `${label} ${i} (${t.x},${t.y}) is outside the map`,
        });
      } else if (m.rows[t.y]?.[t.x] !== '.') {
        ctx.addIssue({
          code: 'custom',
          message: `${label} ${i} (${t.x},${t.y}) is not on open ground`,
        });
      }
    };
    m.starts.forEach((s, i) => check('start', s, i));
    m.mines.forEach((s, i) => check('mine', s, i));
    m.units.forEach((s, i) => check('unit', s, i));
  });

export type MapFile = z.infer<typeof MapFile>;

export interface LoadedMap {
  readonly name: string;
  readonly map: GameMap;
  readonly starts: ReadonlyArray<{ readonly x: number; readonly y: number }>;
  readonly mines: ReadonlyArray<{ readonly x: number; readonly y: number; readonly gold: number }>;
  readonly startingResources: { readonly gold: number; readonly lumber: number };
  readonly units: ReadonlyArray<{
    readonly def: string;
    readonly owner: number;
    readonly x: number;
    readonly y: number;
  }>;
}

/**
 * Validate raw JSON and build the terrain. Buildings (town centers, mines) are placed by
 * the caller through `Sim.spawnBuilding`, which rejects footprints that overlap forest.
 */
export function loadMap(raw: unknown): LoadedMap {
  const file = parseOrThrow(MapFile, raw, 'map file');
  const map = createMap(file.width, file.height);
  file.rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      if (row[x] === 'T') setForest(map, x, y, file.lumberPerTile);
    }
  });
  return {
    name: file.name,
    map,
    starts: file.starts,
    mines: file.mines,
    startingResources: file.startingResources,
    units: file.units,
  };
}
